import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCalendarRange } from "../components/ScheduleCalendar/calendar.utils";
import { CalendarService } from "../services/scheduling/CalendarService";
import { getErrorMessage } from "../services/ErrorMessage";
import {
  CalendarEvent,
  CalendarView,
  ScheduleSummary,
} from "../services/scheduling/Scheduling.types";

export interface ScheduleCalendarController {
  anchorDate: Dayjs;
  view: CalendarView;
  selectedScheduleIds: string[];
  events: CalendarEvent[];
  eventsLoading: boolean;
  eventsError?: string;
  setAnchorDate: (date: Dayjs) => void;
  setView: (view: CalendarView) => void;
  setSelectedScheduleIds: (ids: string[]) => void;
  retryEvents: () => void;
}

export function useScheduleCalendar(
  calendarService: CalendarService,
  schedules: ScheduleSummary[],
): ScheduleCalendarController {
  const [anchorDate, setAnchorDate] = useState(() => dayjs());
  const [view, setView] = useState<CalendarView>("week");
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string>();
  const requestSequence = useRef(0);
  const knownScheduleKey = useRef("");
  const scheduleKey = schedules.map((schedule) => schedule.id).join("|");

  useEffect(() => {
    if (knownScheduleKey.current === scheduleKey) {
      return;
    }

    knownScheduleKey.current = scheduleKey;
    setSelectedScheduleIds(schedules.map((schedule) => schedule.id));
    setEvents([]);
    setEventsError(undefined);
  }, [scheduleKey, schedules]);

  const range = useMemo(
    () => getCalendarRange(anchorDate, view),
    [anchorDate, view],
  );
  const loadEvents = useCallback(async () => {
    const requestId = ++requestSequence.current;

    if (selectedScheduleIds.length === 0) {
      setEvents([]);
      setEventsError(undefined);
      setEventsLoading(false);
      return;
    }

    setEventsLoading(true);
    setEventsError(undefined);

    try {
      const loadedEvents = await calendarService.load(
        selectedScheduleIds,
        schedules,
        range,
      );
      if (requestSequence.current === requestId) {
        setEvents(loadedEvents);
      }
    } catch (error) {
      if (requestSequence.current === requestId) {
        setEvents([]);
        setEventsError(getErrorMessage(error));
      }
    } finally {
      if (requestSequence.current === requestId) {
        setEventsLoading(false);
      }
    }
  }, [calendarService, range, schedules, selectedScheduleIds]);

  useEffect(() => {
    void loadEvents();

    return () => {
      requestSequence.current += 1;
    };
  }, [loadEvents]);

  return {
    anchorDate,
    view,
    selectedScheduleIds,
    events,
    eventsLoading,
    eventsError,
    setAnchorDate,
    setView,
    setSelectedScheduleIds,
    retryEvents: () => void loadEvents(),
  };
}
